// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { LazyImageProps } from "@hardwoods/MobiusOverrides/LazyImage/LazyImage";
import { fontConfig } from "@hardwoods/XDL/PreStyle/typography";
import { ellipsis } from "@hardwoods/XDL/Utilities";
import color from "@hardwoods/XDL/Utilities/color";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import { mergeWithCss } from "@hardwoods/XDL/Utilities/mergeWithCss";
import { lineOnTitleRightSide } from "@hardwoods/XDL/Utilities/SharedStyles";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LinkProps } from "@insite/mobius/Link";
import { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

interface BrandDetailsCategoriesStyle {
    title?: TypographyProps;
    mainContainer?: GridContainerProps;
    innerContainer?: GridContainerProps;
    itemContainer?: GridItemProps;
    imageGridItem?: GridItemProps;
    categoryImage?: LazyImageProps;
    categoryImageLink?: ClickablePresentationProps;
    categoryNameLinkGridItem?: GridItemProps;
    categoryNameLink?: LinkProps;
    subCategoryNameLink?: LinkProps;
    viewMoreLinkItem?: GridItemProps;
    viewMoreLink?: LinkProps;
}

export const categoryNameLinkStyle = (alpha = 0.7): LinkProps => ({
    typographyProps: {
        size: 14,
        transform: "capitalize",
        weight: "bold",
        css: css`
            letter-spacing: 0;
            line-height: 18px;
            font-family: ${fontConfig.headingFontFamily};
            width: 100%;
            overflow-wrap: break-word;
            word-wrap: break-word;

            ${ellipsis(2)}
        `,
    },
    hoverStyle: {
        color: color("text.link", alpha)(),
    },
    css: css`
        width: 100%;
        margin-bottom: 15px;
    `,
});

const brandDetailsCategoriesItemStyle: GridItemProps = {
    width: [12, 6, 4, 3, 3],
    css: css`
        ${xScreen.lte("xs")} {
            justify-content: center;
        }
    `,
};

export const brandDetailsCategoriesStyle: BrandDetailsCategoriesStyle = {
    mainContainer: {
        gap: 20,
        offsetCss: css`
            ${xScreen.lte("xs")} {
                justify-content: center;
            }
        `,
    },
    innerContainer: {
        gap: 0,
        offsetCss: css`
            justify-content: center;
        `,
        css: css`
            padding: 16px;
            flex-direction: column;
            justify-content: center;
            background-color: ${color("common.white")};
            box-shadow: 0 -1px 4px 0 rgba(0, 0, 0, 0.15);
            border-radius: 4px;
            &&& {
                width: 262px;
                height: 100%;
                max-width: 262px;
                min-width: 262px;
            }
            &:hover {
                box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.15);
            }
        `,
    },
    itemContainer: {
        ...brandDetailsCategoriesItemStyle,
    },
    imageGridItem: {
        width: undefined,
        align: "middle",
        css: css`
            width: 230px;
            max-width: 230px;
            max-height: 230px;
            min-height: 230px;
            flex-basis: auto;
            height: auto;
            justify-content: center;
            overflow: hidden;
            &&& {
                margin-bottom: 15px;
            }
        `,
    },
    categoryImageLink: {
        css: css`
            & > ${TypographyStyle} {
                display: flex;
            }

            img {
                height: 100%;
            }
        `,
    },
    categoryImage: {
        cloudinaryTransformations: {
            width: 230,
            quality: "good",
        },
        css: css`
            height: 100%;
            min-height: inherit;
            min-width: inherit;
            img {
                max-height: 230px;
            }
        `,
    },
    categoryNameLinkGridItem: {
        width: 12,
        css: css`
            padding-bottom: 5px;
            flex-basis: auto;
            width: 100%;
        `,
    },
    categoryNameLink: {
        ...categoryNameLinkStyle(),
    },
    subCategoryNameLink: {
        ...mergeWithCss(categoryNameLinkStyle(), {
            typographyProps: {
                size: 12,
                weight: "normal",
                css: css`
                    font-family: ${fontConfig.fontFamily};
                `,
            },
            css: css`
                width: 100%;
                margin-bottom: 8px;
            `,
        }),
    },
    viewMoreLinkItem: {
        width: 12,
        css: css`
            padding-top: 5px;
        `,
    },
    viewMoreLink: {
        color: "secondary",
        typographyProps: {
            size: 13,
            weight: "bold",
            ellipsis: true,
        },
    },
};
